// WHAT: Component displaying statistics overview for Kanban board
// WHY:  Provides quick overview of roadmap metrics (totals, status counts, progress, modules)
// DOMAIN: Admin → Roadmap → Kanban
// DEPENDENCIES: Stack, Card, Badge components, Lucide icons
// RISKS:
//   - NOTE: Uses index as key in map - consider using unique identifier
//   - NOTE: averageProgress is rounded - may lose precision
//   - NOTE: Grid layout may not be responsive on very small screens

import {
  LucideTarget,
  LucideCheckCircle,
  LucideClock,
  LucideAlertTriangle,
  LucideTrendingUp,
  LucideBarChart3,
} from "lucide-react";
import React from "react";

import { Stack, Card, Badge } from "@/components/ui";

/**
 * Props for KanbanStats component
 */
interface KanbanStatsProperties {
  /** Average progress across all items (0-100) */
  averageProgress: number;
  /** Number of done items */
  doneItems: number;
  /** Number of in progress items */
  inProgressItems: number;
  /** Number of unique modules */
  moduleCount: number;
  /** Number of open items */
  openItems: number;
  /** Number of rejected items */
  rejectedItems: number;
  /** Total number of roadmap items */
  totalItems: number;
}

/**
 * WHAT:
 *   Component that displays statistics overview cards for the Kanban board,
 *   showing totals, status counts, average progress, and module count.
 *
 * WHY:
 *   Provides quick visual overview of roadmap metrics to help users understand
 *   the current state of their roadmap at a glance.
 *
 * PROPS:
 *   - totalItems: number — Total item count
 *   - openItems: number — Open status count
 *   - inProgressItems: number — In progress status count
 *   - doneItems: number — Done status count
 *   - rejectedItems: number — Rejected status count
 *   - averageProgress: number — Average progress (0-100)
 *   - moduleCount: number — Number of modules
 *
 * BEHAVIOR:
 *   - Renders 7 statistic cards in responsive grid (2/4/7 columns)
 *   - Each card shows icon, label badge, and value
 *   - Formats average progress as percentage with rounding
 *
 * RISKS:
 *   - NOTE: Index used as key - should use unique identifier if stats array changes
 *   - NOTE: averageProgress rounding may lose precision for small values
 *   - NOTE: Grid layout may cause wrapping issues on very small screens
 *
 * @param root0
 *
 * @param root0.totalItems
 *
 * @param root0.openItems
 *
 * @param root0.inProgressItems
 *
 * @param root0.doneItems
 *
 * @param root0.rejectedItems
 *
 * @param root0.averageProgress
 *
 * @param root0.moduleCount
 *
 * @returns {JSX.Element} Die gerenderte KanbanStats-Komponente
 *
 */
export const KanbanStats: React.FC<KanbanStatsProperties> = ({
  averageProgress,
  doneItems,
  inProgressItems,
  moduleCount,
  openItems,
  rejectedItems,
  totalItems,
}) => {
  const stats = [
    {
      color: "text-slate-300",
      icon: LucideTarget,
      label: "Total Items",
      value: totalItems,
      variant: "default" as const,
    },
    {
      color: "text-tracq-bright-blue",
      icon: LucideTarget,
      label: "Open",
      value: openItems,
      variant: "primary" as const,
    },
    {
      color: "text-tracq-deep-orange",
      icon: LucideClock,
      label: "In Progress",
      value: inProgressItems,
      variant: "accent" as const,
    },
    {
      color: "text-green-500",
      icon: LucideCheckCircle,
      label: "Done",
      value: doneItems,
      variant: "success" as const,
    },
    {
      color: "text-red-500",
      icon: LucideAlertTriangle,
      label: "Rejected",
      value: rejectedItems,
      variant: "destructive" as const,
    },
    {
      color: "text-tracq-deep-orange",
      icon: LucideTrendingUp,
      label: "Avg Progress",
      value: `${String(Math.round(averageProgress))}%`,
      variant: "accent" as const,
    },
    {
      color: "text-slate-300",
      icon: LucideBarChart3,
      label: "Modules",
      value: moduleCount,
      variant: "default" as const,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
      {stats.map((stat) => {
        const IconComponent = stat.icon;
        return (
          <Card
            className="bg-slate-700/30 border-slate-600/50"
            key={stat.label}
            padding="md"
            variant="default"
          >
            <Stack align="center" direction="col" gap={2}>
              <div className="flex items-center gap-2">
                <IconComponent className={`w-5 h-5 ${stat.color}`} />
                <Badge size="sm" style="subtle" variant={stat.variant}>
                  {stat.label}
                </Badge>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{stat.value}</div>
              </div>
            </Stack>
          </Card>
        );
      })}
    </div>
  );
};
